import { FC } from "react";
import { useRouter } from "next/router";
import Link from "next/link";
import viewStyles from "../view.module.css";

import gql from "graphql-tag";
import dunegql from "src/utils/dunegqlCodegen/dunegql";
import { apolloCore, apolloDuneGraphql } from "src/utils/apollo/apollo";
import {
  CatalogTable as CatalogTableType,
  CatalogTableFragment,
  CatalogTableFragmentDoc,
  TableType,
} from "src/gql/codegen/duneGraphql";
import {
  CatalogTableFragment as LegacyCatalogTableFragment,
  CatalogTableFragmentDoc as LegacyCatalogTableFragmentDoc,
  TableType as LegacyTableType,
} from "src/gql/codegen/graphql";
import { LegacyCatalogTable } from "./CatalogTables";

import { Icon } from "src/components/icon/icon";
import { IconList } from "src/components/ui-components/Icons/IconList";
import { IconTableMaterialized } from "src/components/ui-components/Icons/IconTableMaterialized";
import { IconTableUpload } from "src/components/ui-components/Icons/IconTableUpload";

import useAddTableToEditor from "../useAddTableToEditor";
import { formattedFileSize } from "src/utils/fileSize";
import TablePreviewButton from "../TablePreviewButton/TablePreviewButton";
import SearchQueriesButton from "../SearchQueriesButton/SearchQueriesButton";
import { assertUnreachable } from "src/utils/assertUnreachable";

// This is a workaround to support Catalog Table cache lookups from both Hasura and Dune GQL clients
export const getCachedCatalogTable = (
  selectedCatalogTableId: string
): CatalogTableFragment | LegacyCatalogTableFragment | null => {
  const id = `CatalogTable:${selectedCatalogTableId}`;
  return (
    apolloDuneGraphql.readFragment<CatalogTableFragment>({
      id,
      fragment: CatalogTableFragmentDoc,
    }) ??
    // TODO: Remove legacy fragment read after migrating Catalog Table queries to Dune GQL
    apolloCore.readFragment<LegacyCatalogTableFragment>({
      id,
      fragment: LegacyCatalogTableFragmentDoc,
    })
  );
};

const nameDoesNotNeedToBeEscapedRegex =
  /^(?=.{2,}$)[a-zA-Z][a-zA-Z0-9_]*[a-zA-Z0-9]$|^[a-zA-Z]$/;

export function buildFullName({
  catalogName,
  schemaName,
  tableName,
}: Pick<CatalogTableType, "catalogName" | "schemaName" | "tableName">): string {
  const formattedSchemaName = nameDoesNotNeedToBeEscapedRegex.test(schemaName)
    ? schemaName
    : `"${schemaName}"`;
  const formattedTableName = nameDoesNotNeedToBeEscapedRegex.test(tableName)
    ? tableName
    : `"${tableName}"`;
  return `${catalogName}.${formattedSchemaName}.${formattedTableName}`;
}

const getIcon = (tableType: TableType | LegacyTableType) => {
  switch (tableType) {
    case TableType.MaterializedView:
    case LegacyTableType.MaterializedView:
      return <IconTableMaterialized className={viewStyles.item} />;
    case TableType.UserUploadedTable:
    case LegacyTableType.UserUploadedTable:
      return <IconTableUpload className={viewStyles.item} />;
    default:
      assertUnreachable(tableType);
  }
};

type BaseCatalogTableProps = Pick<
  CatalogTableType,
  "schemaName" | "tableName" | "isPrivate"
> & {
  tableType: TableType | LegacyTableType;
  hideSchemaName?: boolean;
};

export const BaseCatalogTable: FC<BaseCatalogTableProps> = ({
  tableType,
  hideSchemaName = false,
  schemaName,
  tableName,
  isPrivate,
}) => (
  <>
    {getIcon(tableType)}
    {!hideSchemaName && (
      <span className={viewStyles.highLevel}>{schemaName}</span>
    )}
    <span className={viewStyles.lowLevel}>{tableName}</span>
    {isPrivate && <span className={viewStyles.abiType}>private</span>}
  </>
);

type CatalogTableProps = {
  table: LegacyCatalogTable;
  hideSchemaName?: boolean;
};

export const CatalogTable: FC<CatalogTableProps> = ({
  table,
  hideSchemaName = false,
}) => {
  const router = useRouter();
  const addTableToEditor = useAddTableToEditor();

  const {
    id,
    catalog_name,
    schema_name,
    table_name,
    table_type,
    table_size_bytes,
  } = table;
  const fullName = buildFullName({
    catalogName: catalog_name,
    schemaName: schema_name,
    tableName: table_name,
  });
  return (
    <li>
      <Link
        href={{
          query: {
            ...router.query,
            id: id,
          },
        }}
        shallow={true}
      >
        <BaseCatalogTable
          tableType={table_type}
          schemaName={table.schema_name}
          tableName={table.table_name}
          isPrivate={table.is_private}
          hideSchemaName={hideSchemaName}
        />
      </Link>
      {typeof table_size_bytes === "number" && (
        <span className={viewStyles.sizeLabel}>
          {formattedFileSize({ sizeInBytes: table_size_bytes })}
          <IconList />
        </span>
      )}
      <TablePreviewButton fullName={fullName} />
      <SearchQueriesButton table_name={fullName} />
      <button
        className={viewStyles.dataExplorerIconButton}
        onClick={(event) => {
          event.stopPropagation();
          addTableToEditor({ fullName });
        }}
      >
        <Icon icon="chevron-double-right" className={viewStyles.expand} />
      </button>
    </li>
  );
};

// Global Catalog Table Fragment for Dune GQL
dunegql`
  fragment CatalogTable on CatalogTable {
    catalogName
    columns {
      name
      nullable
      type
    }
    createdAt
    id
    isPrivate
    lastRefreshedAt
    schemaName
    tableName
    tableSizeBytes
    tableType
    teamId
    updatedAt
    userId
  }
`;

// Global Catalog Table Fragment for Hasura GQL
gql`
  fragment CatalogTable on CatalogTable {
    catalogName: catalog_name
    columns {
      name
      nullable
      type
    }
    createdAt: created_at
    id
    isPrivate: is_private
    schemaName: schema_name
    tableName: table_name
    tableSizeBytes: table_size_bytes
    tableType: table_type
    teamId: team_id
    updatedAt: updated_at
    userId: user_id
  }
`;
